// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/cosmetics/components/RoomOutfitEditor.module.css';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getWebglSupport} from '@app/features/voice_scene/utils/WebglSupport';
import {Component, type ReactNode, useEffect, useState} from 'react';

const logger = new Logger('RoomOutfitStage');

type CharacterStage = typeof import('@app/features/voice_scene/components/RoomCharacterStage').RoomCharacterStage;

// The live 3D character is a chunk of its own (three.js comes with it), fetched the first time a stage shows; until
// it is there, if it fails, or without WebGL 2, the preview image stays.
function useCharacterStage(): CharacterStage | null {
	const [stage, setStage] = useState<CharacterStage | null>(null);
	const on = getWebglSupport().webgl2;
	useEffect(() => {
		if (!on || stage) return;
		let live = true;
		import('@app/features/voice_scene/components/RoomCharacterStage')
			.then((module) => {
				if (live) setStage(() => module.RoomCharacterStage);
			})
			.catch((error: unknown) => logger.warn('Room character stage failed to load, keeping the image', error));
		return () => {
			live = false;
		};
	}, [on, stage]);
	return on ? stage : null;
}

// A stage that cannot start (its WebGL context refused) leaves the preview image in its place.
class StageBoundary extends Component<{fallback: ReactNode; children: ReactNode}, {failed: boolean}> {
	override state = {failed: false};

	static getDerivedStateFromError() {
		return {failed: true};
	}

	override componentDidCatch(error: unknown) {
		logger.warn('Room character stage failed to start, keeping the image', error);
	}

	override render() {
		return this.state.failed ? this.props.fallback : this.props.children;
	}
}

interface RoomOutfitStageProps {
	// Part keys in slot order and the clothes colour the 3D character wears.
	parts: ReadonlyArray<string>;
	shirt: number;
	// The manifest preview shown until the 3D character is there, and instead of it without WebGL 2.
	imageUrl: string | null;
	// A whole figure (a look or a character) or one part's close-up.
	kind: 'look' | 'part';
	caption: string | null;
}

export function RoomOutfitStage({parts, shirt, imageUrl, kind, caption}: RoomOutfitStageProps) {
	const CharacterStage = useCharacterStage();
	const image = imageUrl && (
		<img
			key={imageUrl}
			className={styles.stageImage}
			src={imageUrl}
			alt=""
			draggable={false}
			data-flx="cosmetics.room-outfit-stage.image"
		/>
	);
	return (
		<figure className={styles.stage} data-kind={kind} data-flx="cosmetics.room-outfit-stage.stage">
			<div className={styles.stageLight} aria-hidden data-flx="cosmetics.room-outfit-stage.light" />
			{CharacterStage ? (
				<StageBoundary fallback={image} data-flx="cosmetics.room-outfit-stage.boundary">
					<CharacterStage
						parts={parts}
						shirt={shirt}
						className={styles.stage3d}
						data-flx="cosmetics.room-outfit-stage.character"
					/>
				</StageBoundary>
			) : (
				image
			)}
			{caption && (
				<figcaption className={styles.stageCaption} data-flx="cosmetics.room-outfit-stage.caption">
					{caption}
				</figcaption>
			)}
		</figure>
	);
}
